import { useCallback } from "react";
import { PageRange } from "../Models/evaluation";
import { Document } from "../Models/company";
import {
  removeAnnexDocument,
  insertAnnexDocument,
  addAnnexSubsectionToParent,
  updateAnnexDocument,
  addAnnexSection,
  addAnnexAvailableDocument,
  addAnnexAvailableSubsection,
  findAnnexDocument,
} from "../Helpers/annexActions";

export interface AnnexDocument {
  id: number;
  name: string;
  originalName: string;
  order?: number | string;
  pageRanges: PageRange[] | null;
  placeholder_id?: string;
  type: "document" | "title" | "empty_section";
  number_of_pages?: number | null;
  subsections?: AnnexDocument[];
}

export function useAnnexActions(
  setAnnex: React.Dispatch<React.SetStateAction<AnnexDocument[]>>
) {
  const removeDocument = useCallback(
    (docId: number) => {
      setAnnex((prev) => removeAnnexDocument(prev, docId));
    },
    [setAnnex]
  );

  const addAnnexSubsection = useCallback(
    (parentId: number, docId: number) => {
      setAnnex((prev) => {
        const document = findAnnexDocument(prev, docId);

        if (!document) return prev;

        const result = removeAnnexDocument(prev, docId);

        return addAnnexSubsectionToParent(result, parentId, document);
      });
    },
    [setAnnex]
  );

  const insertDocument = useCallback(
    (document: AnnexDocument, insertAtId: number) => {
      setAnnex((prev) => insertAnnexDocument(prev, document, insertAtId));
    },
    [setAnnex]
  );

  const swapDocuments = useCallback(
    (docId1: number, docId2: number) => {
      setAnnex((prev) => {
        const document = findAnnexDocument(prev, docId1);

        if (!document) return prev;

        const result = removeAnnexDocument(prev, docId1);

        return insertAnnexDocument(result, document, docId2);
      });
    },
    [setAnnex]
  );

  const addAvailableDocument = useCallback(
    (doc: Document, droppedOnId?: number) => {
      setAnnex((prev) => {
        let result = addAnnexAvailableDocument(prev, doc);

        if (droppedOnId) {
          const document = findAnnexDocument(result, doc.id);

          if (document) {
            result = removeAnnexDocument(result, doc.id);
            result = insertAnnexDocument(result, document, droppedOnId);
          }
        }

        return result;
      });
    },
    [setAnnex]
  );

  const addAvailableSubsection = useCallback(
    (parentId: number, doc: Document) => {
      setAnnex((prev) => addAnnexAvailableSubsection(prev, parentId, doc));
    },
    [setAnnex]
  );

  const updateDocument = useCallback(
    (
      docId: number,
      updates: {
        name?: string;
        pageRanges?: PageRange[] | null;
      }
    ) => {
      setAnnex((prev) => updateAnnexDocument(prev, docId, updates));
    },
    [setAnnex]
  );

  const addSection = useCallback(
    (
      type: "title" | "empty_section",
      options?: { title?: string; placeholder_id?: string; order?: number }
    ) => {
      setAnnex((prev) => addAnnexSection(prev, type, options));
    },
    [setAnnex]
  );

  return {
    addAvailableSubsection,
    addAnnexSubsection,
    removeDocument,
    swapDocuments,
    updateDocument,
    addSection,
    insertDocument,
    addAvailableDocument,
  };
}
